import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Typography, Row, Card, Col, Button, Pagination } from 'antd';
import { SiGooglecalendar } from 'react-icons/si';
import '../styles/Eventos.css';
import defaultImage from '../images/image.png'; // Import the default image

const { Title, Text } = Typography;

const Eventos = () => {
  const [events, setEvents] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [fetchCount, setFetchCount] = useState(0); // Track fetch attempts
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const eventsPerPage = 3; // Number of events per page
  const maxFetchAttempts = 5; // Limit the number of fetch attempts

  // Define calendarId and apiKey as constants to ensure they don't change
  const calendarId = process.env.REACT_APP_GOOGLE_CALENDAR_ID;
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  // Fetch events from Google Calendar API
  useEffect(() => {
    if (fetchCount >= maxFetchAttempts) return; // Stop fetching after max attempts

    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`,
          {
            params: {
              key: apiKey,
              timeMin: new Date().toISOString(),
              maxResults: 10,
              singleEvents: true,
              orderBy: 'startTime',
            },
          }
        );

        const processedEvents = response.data.items.map((event) => {
          const fileUrl = event.attachments?.[0]?.fileUrl || '';
          const fileId = fileUrl.split('id=')[1] || '';
          return {
            ...event,
            fileId,
          };
        });

        setEvents(processedEvents);
        setFetchCount((prev) => prev + 1); // Increment fetch count
      } catch (error) {
        console.error('Error fetching events:', error);
        setHasError(true);
        setFetchCount((prev) => prev + 1); // Increment fetch count even on error
      }
    };

    fetchEvents();
  }, [fetchCount, maxFetchAttempts, calendarId, apiKey]);

  // Memoize the events to prevent unnecessary re-renders
  const memoizedEvents = useMemo(() => events, [events]);

  // Memoize the current events for pagination
  const currentEvents = useMemo(() => {
    const indexOfLastEvent = currentPage * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    return memoizedEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  }, [memoizedEvents, currentPage, eventsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // If there are no events or an error occurred, return null
  if (events.length === 0 || hasError) {
    return null;
  }

  return (
    <div className="main-container" id="eventos">
      <Row justify="center">
        <Title level={2} className="title-responsive">
          Calendario de eventos
        </Title>
      </Row>
      <div className="event-list">
        <Row justify="center">
          {currentEvents.map((event) => {
            const startDate = event.start?.dateTime
              ? new Date(event.start.dateTime)
              : null;
            const endDate = event.end?.dateTime
              ? new Date(event.end.dateTime)
              : null;

            const isValidDate = startDate && !isNaN(startDate.getTime());

            return (
              <Col
                key={event.id}
                xs={21} /* Span de 21 in smartphones */
                lg={15} /* Span of 15 in desktops */
              >
                <Card
                  hoverable
                  className="horizontal-card"
                  style={{ width: '100%' }}
                >
                  <div className="card-content">
                    {/* Image Section */}
                    <div className="image-container">
                      <img
                        src={
                          event.fileId
                            ? `https://drive.google.com/thumbnail?id=${event.fileId}&sz=s1000`
                            : defaultImage
                        }
                        alt="eventLogo"
                        className="card-image"
                        onError={(e) => {
                          e.target.onerror = null; // Evita un bucle infinito si la imagen predeterminada falla
                          e.target.src = defaultImage; // Reemplaza con la imagen predeterminada
                        }}
                      />

                      {/* Date Overlay */}
                      {isValidDate && (
                        <div className="date-overlay">
                          <span className="month">
                            {startDate
                              .toLocaleString('en-US', {
                                month: 'short',
                              })
                              .toUpperCase()}
                          </span>
                          <span className="day">{startDate.getDate()}</span>
                        </div>
                      )}
                    </div>

                    {/* Text Section */}
                    <div className="card-text">
                      <Text
                        strong
                        className="card-title"
                        style={{
                          fontFamily: 'Outfit',
                          color: 'rgba(0, 0, 0, 0.85)',
                        }}
                      >
                        {event.summary}
                      </Text>
                      <Text
                        type="secondary"
                        className="card-date"
                        style={{
                          fontFamily: 'Roboto',
                        }}
                      >
                        {isValidDate
                          ? `${startDate.toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })} de ${startDate.toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })} a ${endDate.toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : 'Fecha y horario no determinado'}
                      </Text>
                      <Text
                        className="card-description"
                        style={{
                          fontFamily: 'Roboto',
                          color: '#515151',
                        }}
                      >
                        {event.description?.substring(0, 300) ||
                          'Este evento no posee descripcion.'}
                      </Text>
                      <div className="button-container">
                        <Button
                          icon={<SiGooglecalendar />}
                          type="primary"
                          size="large"
                          className="card-button"
                          href={`https://calendar.google.com/calendar/event?action=TEMPLATE&tmeid=${event.htmlLink.split('eid=')[1]}&tmsrc=${calendarId.replace(
                            '@',
                            '%40'
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Agendar evento
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
        <Row justify="center">
          {memoizedEvents.length >= eventsPerPage && (
            <div className="pagination-container">
              <Pagination
                current={currentPage}
                pageSize={eventsPerPage}
                total={memoizedEvents.length}
                onChange={handlePageChange}
              />
            </div>
          )}
        </Row>
      </div>
    </div>
  );
};

export default Eventos;
